import React, { useMemo, useState } from "react"
import { graphql } from "gatsby"
import styled from "styled-components"

import Layout from "../components/common/Layout"
import SEO from "../components/common/Seo"
import ListItemTeam from "../components/pages/about/ListItemTeam"

import Col from "react-bootstrap/Col"

import { ITeamFormer, IMember } from "../types/team"
import ExpandSection from "../components/pages/about/ExpandSection"
import media from "../helpers/media"
import { useRenderOn } from "../hooks/useRenderOn"
import Image, { GatsbyImageProps } from "gatsby-image"

/**
 * Styles
 */
const Intro = styled(Col)`
  font-family: var(--font-secondary);
  font-size: var(--font-size-md);
  margin: 25px 0px;

  && {
    padding: 0;
  }

  b {
    font-family: var(--font-primary);
    font-weight: 400;
    font-size: var(--font-size-sm);
  }

  ${media.tablet} {
    margin: 0;
    font-size: 20px;

    b {
      font-size: 17px;
    }
  }

  ${media.laptop} {
    && {
      padding-left: 30px;
    }
  }
`

const IntroFocus = styled(Intro)`
  font-family: var(--font-secondary);
  font-size: 20px;
  padding: 8px 15px 25px 15px;

  margin-bottom: 0;

  b {
    font-family: var(--font-primary);
    font-weight: 400;
    font-size: 18px;
  }

  && {
    padding: 0;
  }

  ${media.tablet} {
    margin: 0;
    font-size: 25px;

    b {
      font-size: 22px;
    }
  }
`

const Section = styled.section`
  margin: 0;

  ${media.tablet} {
    margin: 30px 0;
  }

  &:last-child {
    padding-bottom: 30px;
  }
`

const Box = styled.div`
  padding: 10px 0;
  position: relative;
`

const TitleSection = styled.h3`
  border-bottom: 1px solid var(--color-grey-1);
  padding: 10px 0;
  text-transform: lowercase;
`

const List = styled.ul`
  list-style: none;
  column-count: 5;
  column-width: 230px;
  padding: 0;
`

const PortraitHoverWrapper = styled.div`
  position: absolute;
  top: -100%;
  left: 0;
`

const PSerif = styled.p`
  font-family: var(--font-secondary);
  font-size: var(--font-size-lg);
  margin: 0;
`

const SectionRewards = styled(Section)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  .gatsby-image-wrapper {
    & > div,
    & > img {
      display: none;
    }
  }
`

interface IMembersByYear {
  [key: string]: IMember[]
}

type DataProps = {
  site: {
    buildTime: string
  }
  allContentfulTeam: {
    nodes: IMember[]
  }
  contentfulGlobalContent: {
    aboutIntro1?: {
      childContentfulRichText: {
        html?: string
      }
    }
    aboutIntro2?: {
      childContentfulRichText: {
        html?: string
      }
    }
    aboutRewards?: GatsbyImageProps[]
  }
}

interface IProps {
  data: DataProps
  path: string
}

/**
 * Constants
 */
const DURATION = 0.6

const About = ({ data, path }: IProps) => {
  const { nodes: team } = data.allContentfulTeam
  const {
    aboutIntro1,
    aboutIntro2,
    aboutRewards: rewards,
  } = data.contentfulGlobalContent

  const intro1 = aboutIntro1?.childContentfulRichText.html
  const intro2 = aboutIntro2?.childContentfulRichText.html

  /**
   * Handle portrait section
   */
  const [selectedMember, setSelectedMember] = useState<IMember>()
  const [portraitOpen, setPortraitOpen] = useState<boolean>(false)

  const handleMemberClick = (member: IMember): void => {
    // Click on the same name, close portrait
    if (
      member.name + member.firstName ===
      (selectedMember?.name || "") + (selectedMember?.firstName || "")
    ) {
      setPortraitOpen(false)

      setTimeout(() => {
        setSelectedMember(undefined)
      }, DURATION * 1000)
      return
    }

    // Click on anther name, show new portrait
    setPortraitOpen(true)
    setSelectedMember(member)
  }

  /**
   * Team with current members
   */
  const teamCurrent: IMember[] = useMemo(
    () => team.filter(member => member.isCurrent),
    []
  )

  /**
   * Team with former members by year
   */
  const teamFormer: ITeamFormer[] = useMemo(() => {
    const membersByYear: IMembersByYear = {}
    const former = team.filter(member => !member.isCurrent)

    former.forEach(member => {
      if (!member.year) {
        return
      }

      if (membersByYear[member.year]) {
        membersByYear[member.year].push(member)
      } else {
        membersByYear[member.year] = [member]
      }
    })

    return Object.entries(membersByYear).map(item => {
      return {
        year: item[0],
        members: item[1],
      }
    })
  }, [])

  // responsive
  const { mdOrUp } = useRenderOn()

  return (
    <Layout title={mdOrUp ? "qui est georges ?" : ""} container="fluid">
      <SEO title="équipe" />
      <Section className="row">
        <IntroFocus lg={6}>
          {intro1 && (
            <div
              dangerouslySetInnerHTML={{
                __html: intro1,
              }}
            />
          )}
        </IntroFocus>
        <Intro lg={6}>
          {intro2 && (
            <div
              dangerouslySetInnerHTML={{
                __html: intro2,
              }}
            />
          )}
        </Intro>
      </Section>
      {rewards?.length && (
        <SectionRewards>
          {rewards.map((reward, i) => (
            <Image
              fluid={reward.fluid}
              imgStyle={{
                height: "auto",
                width: "initial",
                maxWidth: "100%",
                objectFit: "contain",
                position: "static",
              }}
              alt={`rewards-${i}`}
              key={i}
            />
          ))}
        </SectionRewards>
      )}
      <Section>
        <TitleSection>équipe</TitleSection>
        <Box>
          <List>
            {teamCurrent.map((member: IMember, i) => (
              <ListItemTeam
                key={i}
                title={`${member.firstName} ${member.name}`}
                member={member}
                onClick={() => handleMemberClick(member)}
                className="team-current"
              />
            ))}
          </List>
        </Box>
        <ExpandSection
          duration={DURATION}
          selectedMember={selectedMember}
          portraitOpen={portraitOpen}
        />
      </Section>
      <Section>
        <TitleSection>Les anciens Georges</TitleSection>
        <Box>
          <List>
            {teamFormer.map(({ year, members }: ITeamFormer, i) => (
              <ListItemTeam
                key={i}
                title={year}
                teamByYear={members}
                className="team-former"
              />
            ))}
          </List>
          <PortraitHoverWrapper id="portraitHoverWrapper" />
        </Box>
      </Section>
      <Section>
        <TitleSection>Devenir Georges</TitleSection>

        <Box>
          <PSerif>
            à la recherche d’un stage ou d’une collaboration ?
          </PSerif>
          <PSerif>
            Merci d’envoyer vos CV + BOOK à :
            recrutement[at]ateliergeorges.fr
          </PSerif>
        </Box>
      </Section>
    </Layout>
  )
}

export default About

export const query = graphql`
  {
    site {
      buildTime(formatString: "YYYY-MM-DD hh:mm a z")
    }
    contentfulGlobalContent(name: { eq: "globalContent" }) {
      aboutIntro1 {
        childContentfulRichText {
          html
        }
      }
      aboutIntro2 {
        childContentfulRichText {
          html
        }
      }
      aboutRewards {
        fluid(maxWidth: 450) {
          aspectRatio
          src
          srcSet
          sizes
          ...GatsbyContentfulFluid
        }
      }
    }
    allContentfulTeam {
      totalCount
      nodes {
        id
        name
        firstName
        isCurrent
        roles
        description {
          childContentfulRichText {
            html
          }
        }
        year
        portrait {
          fluid(maxWidth: 280) {
            aspectRatio
            src
            srcSet
            sizes
            ...GatsbyContentfulFluid
          }
        }
      }
    }
  }
`
